import ServicePageTemplate from './ServicePageTemplate';

const customSoftwareData = {
  metaTitle: 'Custom Software Development Dubai | Software Company UAE',
  metaDescription: 'Ascali develops scalable custom software solutions in Dubai, UAE tailored to your business requirements, helping organizations automate processes.',
  bannerGradient: 'from-cyan-600 via-blue-600 to-blue-700',
  category: 'Software Engineering',
  bannerTitle: 'Custom Software Engineered Precisely for Your Business',
  bannerSubtitle: 'Bespoke software solutions built from the ground up to solve your unique challenges, streamline operations, and unlock growth that off-the-shelf products simply cannot deliver.',
  icon: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  stats: [
    { value: '150+', label: 'Custom Solutions Built' },
    { value: '30%', label: 'Faster Time-to-Market' },
    { value: '8+', label: 'Years of Experience' },
    { value: '100%', label: 'Tailored to You' }
  ],
  overview: {
    title: 'Software Built Around Your Business, Not the Other Way Around',
    description: 'Generic software forces you to adapt your processes to its limitations. Custom software gives you complete control — purpose-built workflows, seamless integrations, and a system that scales exactly as your business does. Ascali engineers bespoke solutions using clean architecture and modern engineering principles.',
    highlights: [
      'Purpose-built for your exact workflow and business logic',
      'Full ownership of source code and intellectual property',
      'Seamless integration with your existing systems and tools',
      'Scales with your growth without expensive licensing fees',
      'Ongoing enhancements without vendor dependency'
    ]
  },
  deliverables: [
    'Technical Architecture Blueprint',
    'Full Source Code & Ownership',
    'Comprehensive Documentation',
    'Automated Test Suites',
    'Deployment & DevOps Setup',
    'API Integration Layer',
    'Ongoing Maintenance Plan'
  ],
  benefits: [
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" /></svg>,
      title: 'Perfect Business Fit',
      description: 'Every feature, workflow, and integration is designed around how your team actually works — no compromises, no workarounds.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>,
      title: 'Scalable Architecture',
      description: 'Built on microservices or modular monolith patterns that scale horizontally as user demand grows — no re-architecting required.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>,
      title: 'Seamless Integration',
      description: 'APIs and connectors that link your new software with existing ERP, CRM, payment gateways, and third-party tools effortlessly.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
      title: 'Full IP Ownership',
      description: 'You own 100% of the source code, documentation, and intellectual property — no vendor lock-in, no licensing surprises.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      title: 'Quality-First Development',
      description: 'Automated unit, integration, and end-to-end tests guarantee reliability. Code reviews and clean architecture ensure long-term maintainability.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
      title: 'Long-Term Cost Efficiency',
      description: 'Eliminate recurring SaaS licensing fees. Custom software pays for itself many times over as your user base and features grow.'
    }
  ],
  process: [
    { step: '01', title: 'Discovery & Requirements', description: 'Deep-dive workshops to map your business processes, pain points, and technical requirements into a clear specification.' },
    { step: '02', title: 'Architecture & Design', description: 'System architecture, database design, API contracts, and UI/UX wireframes are reviewed and approved before coding starts.' },
    { step: '03', title: 'Agile Development', description: 'Bi-weekly sprint demos keep you in the loop. Continuous integration ensures code quality at every commit.' },
    { step: '04', title: 'Testing & Deployment', description: 'Comprehensive QA, UAT, performance testing, and a staged rollout ensure a smooth, risk-free go-live.' }
  ],
  technologies: ['Java', '.NET / C#', 'Python', 'Go', 'Node.js', 'Microservices', 'PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'RabbitMQ', 'Kafka', 'Docker', 'Kubernetes', 'AWS', 'Azure'],
  cta: {
    title: 'Ready for Software Built Exactly for Your Business?',
    subtitle: 'Stop fitting your processes into generic tools. Let Ascali engineer a custom solution that gives your team a real competitive edge.'
  },
  faqs: [
    {
      question: 'How is custom software different from buying an off-the-shelf product?',
      answer: 'Off-the-shelf software is built for the average use case and forces your team to adapt. Custom software is built specifically for your workflows, giving you only the features you need, full integration with your existing systems, and the ability to evolve exactly as your business grows — without per-seat licensing costs.'
    },
    {
      question: 'How much does custom software development cost?',
      answer: 'Costs depend on complexity, integrations, and scope. A focused internal tool may start from $20,000–$50,000, while a full enterprise platform can exceed $200,000. We provide a detailed, fixed-scope estimate after the discovery phase so there are no surprises. Custom software typically delivers a strong ROI by eliminating licensing fees and inefficient manual processes.'
    },
    {
      question: 'How long does the development process take?',
      answer: 'Simple applications can take 2–3 months, while complex enterprise systems typically take 6–12 months. We use an agile approach, so you receive working software in short sprints and can adjust priorities as the project evolves.'
    },
    {
      question: 'Who owns the code when the project is done?',
      answer: 'You do — 100%. Upon final payment, all source code, documentation, and intellectual property rights transfer to you. There are no licensing fees, no ongoing dependencies on us, and you can hand the codebase to any developer in the future.'
    },
    {
      question: 'Can you modernize our existing legacy software?',
      answer: 'Yes, legacy modernization is one of our core specialties. We assess your existing system, plan a phased migration to modern technologies, and ensure business continuity throughout. We can re-architect, re-platform, or fully rebuild depending on what makes the most business sense.'
    },
    {
      question: 'Do you provide support after the software goes live?',
      answer: 'Absolutely. We offer flexible support and maintenance packages that include bug fixes, security patches, performance optimization, and feature development. Many of our clients maintain long-term partnerships with us for ongoing product evolution.'
    }
  ]
};

const CustomSoftwarePage = () => <ServicePageTemplate service={customSoftwareData} />;
export default CustomSoftwarePage;
